import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const STORAGE_KEY = "flyt-active-family";

export function useFamily() {
  const { user } = useAuth();

  const { data: memberships = [], isLoading, isError } = useQuery({
    queryKey: ["family-memberships", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("family_members")
        .select("*, families(*)")
        .eq("user_id", user.id)
        .order("created_at");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
    retry: 2,
  });

  // Active family ID persisted in localStorage
  const [activeFamilyId, setActiveFamilyIdState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY) ?? null;
  });

  // Whenever memberships load, ensure active family is valid
  useEffect(() => {
    if (memberships.length === 0) return;
    const valid = memberships.some((m) => m.family_id === activeFamilyId);
    if (!valid) {
      const first = memberships[0].family_id;
      localStorage.setItem(STORAGE_KEY, first);
      setActiveFamilyIdState(first);
    }
  }, [memberships, activeFamilyId]);

  function setActiveFamilyId(id: string) {
    localStorage.setItem(STORAGE_KEY, id);
    setActiveFamilyIdState(id);
  }

  const activeMembership = memberships.find((m) => m.family_id === activeFamilyId)
    ?? memberships[0]
    ?? null;

  const family = activeMembership
    ? (activeMembership.families as any) ?? null
    : null;

  return {
    memberships,
    membership: activeMembership,
    family,
    familyId: activeMembership?.family_id ?? null,
    timezone: (family as any)?.timezone ?? "Europe/Oslo",
    isLoading,
    isError,
    hasFamily: memberships.length > 0,
    hasManyFamilies: memberships.length > 1,
    setActiveFamilyId,
  };
}
